import { inject, Injectable } from '@angular/core';
import { SetControlValuePayload } from '@dat/iq-controls';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';
import { isEmpty, isEqual, isNil, kebabCase, omitBy } from 'lodash';
import { DateTime } from 'luxon';
import { MCDashboardIds } from '@dat/iq-mc-utils';
import {
    DirectionControls,
    Directions,
    EquipmentTypeControls,
    EquipmentTypes,
    GeoTypeControls,
    GeoTypeRouteMap,
    GeoTypes,
    McQuery,
    RouteParams,
    TimeframeControls,
    TimeframeRouteMap,
    TimeFrames
} from './iq-mc-event-bus-state.models';
import { IqMcEventBusStateService } from './iq-mc-event-bus-state.service';

@Injectable({
    providedIn: 'root',
})
export class IqMcEventBusRouteService {
    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _mcService: IqMcEventBusStateService = inject(
        IqMcEventBusStateService,
    );

    constructor() {
        this._bus.registerMap({
            'type:mc-controls:change': {
                callback: async ({ query }: { query: McQuery }) => {
                    await this.handleMcControlsChange(query);
                },
            },
            'type:mc:route:update': {
                callback: async ({
                    routePayload,
                }: {
                    routePayload: {
                        dashboardId: string;
                        routeObject: RouteParams;
                    }[];
                }) => {
                    this.handleMcRouteUpdate(routePayload);
                },
            },
            appendDaysForward: {
                callback: ({
                    id,
                    daysForward,
                }: {
                    id: string;
                    daysForward: string;
                }) => {
                    this.appendDaysForward(id, daysForward);
                },
            },
        });
    }

    async handleMcControlsChange(query: McQuery) {
        const prevQuery: McQuery = this._mcService.getQueryById(query.id)();
        const geoCode = await this.parseGeoCode(query, prevQuery);
        const daysForward =
            query.daysForward ||
            (query.timeframe === TimeFrames.EightDayForecast
                ? prevQuery?.daysForward
                : '0');
        const updatedQuery = {
            ...query,
            geoCode,
            daysForward,
            forecastDate: DateTime.now()
                .setZone('America/Los_Angeles')
                .plus({ days: Number(daysForward) })
                .toFormat('yyyy-MM-dd'),
        };
        const route: string = this.generateRouteIdFromQuery(updatedQuery);
        this.dispatchQueryUpdate(updatedQuery.id, updatedQuery);
        this.dispatchLocationStatus(updatedQuery);
        this.dispatchMcPageRouteResolver(updatedQuery.id, route);
        this.dispatchSegmentEvents(updatedQuery);
    }

    async parseGeoCode(
        query: McQuery,
        prevQuery: McQuery,
    ): Promise<string | undefined> {
        if (!!query.geoCode && isEmpty(query.displayName)) return query.geoCode;
        if (
            isEmpty(query.displayName) &&
            isEmpty(prevQuery?.displayName) &&
            prevQuery?.geoType === query.geoType
        )
            return prevQuery?.geoCode;
        const geoCode = this._mcService.getGeoCodeForQuery(query);
        if (!isEmpty(geoCode)) return geoCode;
        return query.geoCode;
    }

    //TODO: need some clean up for this
    generateRouteIdFromQuery(query: McQuery): string {
        const routeParams = [
            query.equipmentType ?? 'VAN',
            GeoTypeRouteMap[query.geoType] ?? GeoTypeRouteMap[GeoTypes.Market],
            TimeframeRouteMap[query.timeframe] ??
                TimeframeRouteMap[TimeFrames.Current],
            query.direction ?? 'OUT',
            query.geoCode ?? '',
            kebabCase(query.displayName) ?? '',
            query.daysForward ?? '0',
        ];
        return routeParams.join('~');
    }

    async handleMcRouteUpdate(
        routePayload: { dashboardId: string; routeObject: RouteParams }[],
    ) {
        routePayload.forEach(
            async ({
                dashboardId,
                routeObject,
            }: {
                dashboardId: string;
                routeObject: RouteParams;
            }) => {
                routeObject.geoCode =
                    await this._mcService.getGeoCodeForQuery(routeObject);
                const query = this._buildQuery(dashboardId, routeObject);
                const prevQuery = this._mcService.getQueryById(query.id)();
                if (this.areMcQueriesEqual(query, prevQuery)) {
                    return;
                }
                const controls = this._buildControls(dashboardId, routeObject);
                this.dispatchQuery(dashboardId, query);
                this.dispatchControlEvents(controls);
                this.dispatchLocationStatus(query);
            },
        );
    }

    appendDaysForward(dashbaordId: string, daysForward: string) {
        const prevQuery = this._mcService.getQueryById(dashbaordId)();
        if (!prevQuery) return;
        const query = {
            ...prevQuery,
            daysForward,
        };
        const routeId = this.generateRouteIdFromQuery(query);
        this.dispatchMcPageRouteResolver(query.id, routeId);
    }

    private _buildQuery(
        dashboardId: string,
        routeParams: RouteParams,
    ): McQuery {
        return {
            id: dashboardId,
            type: 'area',
            direction: DirectionControls[routeParams.direction]
                .value as Directions,
            geoType: GeoTypeControls[routeParams.geoType].value as GeoTypes,
            geoCode: routeParams.geoCode,
            displayName: routeParams.displayName,
            equipmentType: EquipmentTypeControls[routeParams.equipmentType]
                .value as EquipmentTypes,
            timeframe: TimeframeControls[routeParams.timeframe]
                .value as TimeFrames,
            daysForward: `${routeParams.daysForward}`,
            forecastDate: DateTime.now()
                .setZone('America/Los_Angeles')
                .plus({ days: routeParams.daysForward })
                .toFormat('yyyy-MM-dd'),
        } as McQuery;
    }

    private areMcQueriesEqual(queryA: McQuery, queryB: McQuery): boolean {
        const filteredQueryA = omitBy(
            queryA,
            (value, key) =>
                (key === 'geoCode' || key === 'displayName') && isEmpty(value),
        );
        const filteredQueryB = omitBy(
            queryB,
            (value, key) =>
                (key === 'geoCode' || key === 'displayName') && isEmpty(value),
        );

        return isEqual(filteredQueryA, filteredQueryB);
    }

    private _buildControls(
        dashboardId: string,
        routeParams: RouteParams,
    ): SetControlValuePayload[] {
        let controls: SetControlValuePayload[] = [
            {
                dashboardControlId: `${dashboardId}-search-controls-bar`,
                controlName: 'equipmentType',
                value: EquipmentTypeControls[routeParams.equipmentType],
            },
            {
                dashboardControlId: `${dashboardId}-search-controls-bar`,
                controlName: 'geographyType',
                value: GeoTypeControls[routeParams.geoType],
            },
            {
                dashboardControlId: `${dashboardId}-search-controls-bar`,
                controlName: 'timeType',
                value: TimeframeControls[routeParams.timeframe],
            },
            {
                dashboardControlId: `${dashboardId}-search-controls-bar`,
                controlName: 'direction',
                value: DirectionControls[routeParams.direction],
            },
        ];
        if (!isEmpty(routeParams.displayName)) {
            controls.push({
                dashboardControlId: `${dashboardId}-search-controls-bar`,
                controlName: 'geography',
                value: routeParams.displayName,
            });
        }
        if (routeParams.timeframe === 'FORECAST') {
            const daysForward = routeParams.daysForward;
            controls.push({
                dashboardControlId: `${dashboardId}-days-forward-control`,
                controlName: 'daysForward',
                value: {
                    value: this.getForecastDates(dashboardId)[daysForward]
                        .value,
                    display:
                        this.getForecastDates(dashboardId)[daysForward].display,
                },
            });
        } else {
            controls.push({
                dashboardControlId: `${dashboardId}-days-forward-control`,
                controlName: 'daysForward',
                value: {
                    value: this.getForecastDates(dashboardId)[0].value,
                    display: this.getForecastDates(dashboardId)[0].display,
                },
            });
        }
        return controls;
    }

    dispatchControlEvents(controls: SetControlValuePayload[]) {
        controls.forEach((payload) => {
            this._bus.dispatch({
                name: 'setDashboardControlValue',
                payload,
            });
        });
    }

    dispatchRoute(path: string) {
        this._bus.dispatch({
            name: 'navigateToPath',
            payload: {
                path: ['market-conditions', 'conditions', path],
            },
        });
    }

    dispatchLocationStatus(query: McQuery) {

        const isLocationAvailable =
            !!query && !isNil(query.geoCode) && !isEmpty(String(query.geoCode));
      
        this._bus.dispatch({
            name: 'type:location:status',
            payload: {
                id: query.id,
                available: isLocationAvailable,
                timeframe: query.timeframe,
            },
        });
    }

    dispatchMcPageRouteResolver(dashboardId: string, route: string) {
        this._bus.dispatch({
            name: 'type:mc:page:route:resolver',
            payload: {
                dashboardId,
                route,
            },
        });
    }

    dispatchQuery(dashboardId: string, query: McQuery) {
        this._bus.dispatch({
            name: 'type:query',
            payload: {
                id: dashboardId,
                query,
            },
        });
    }

    dispatchQueryUpdate(dashbaordId: string, query: McQuery) {
        this._bus.dispatch({
            name: 'type:query:update',
            payload: {
                id: dashbaordId,
                query,
            },
        });
    }

    //TODO: need to move this to a utility library
    getForecastDates(dashboardId: string, days: number = 8) {
        const currentDate = DateTime.now().setZone('America/Los_Angeles');
        return Array.from({ length: days }, (_, i) => {
            const forecastDate = currentDate.plus({ days: i });
            const id = `${dashboardId}-Cy_MC_Forecast_Date_${i}`;
            return {
                id: `${kebabCase(id)}`,
                value: i,
                display: `${forecastDate.toFormat(
                    'ccc',
                )} ${forecastDate.toFormat('MM/dd')}`,
            };
        });
    }

    dispatchSegmentEvents(query: McQuery) {
        if(!(query && query.type === 'area')) return;
        const DashboardIdToPanelId = {
            [MCDashboardIds.SingleSearchDashboard]: 'single',
            [MCDashboardIds.DoubleSearchDashboardLeft]: 'left',
            [MCDashboardIds.DoubleSearchDashboardRight]: 'right',
        };
        const panelId = DashboardIdToPanelId[query.id];
        this._bus.dispatch({
            name: 'type:tracking:log',
            payload: {
                type: 'SEGMENT',
                eventType: 'TRACK',
                eventName: 'Market Conditions Results Updated',
                interaction: 'updated',
                props: {
                    geo_code: query.geoCode,
                    filter_equipment_class: query.equipmentType,
                    filter_in_out_bound: query.direction,
                    filter_location: query.displayName,
                    filter_market_area: query.geoType,
                    filter_timeframe: query.timeframe,
                    user_class: this._mcService.getUserTier(),
                    panel_id: panelId,
                },
            },
        });
    }
}
